import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Instagram, Youtube, Twitter, Facebook, Mail, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOGO } from "@/lib/assets";

export default function Footer() {
  const [settings, setSettings] = useState({});
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe");
    } finally { setBusy(false); }
  };

  const donate = settings.gofundme_url || "https://gofund.me/15d8987b7";

  return (
    <footer className="bg-[#ECECE8] text-[#4F6B54] mt-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <img src={LOGO} alt="Refugium Ethiopia" className="h-28 w-auto mb-5 drop-shadow-[0_3px_8px_rgba(31,77,46,0.2)]" />
          <p className="text-[#4F6B54] leading-relaxed max-w-md">A home, a school, and a future for vulnerable children — built in memory of Mercy, sustained by people who refuse to look away.</p>
          <p className="text-[#E0A526] text-xs tracking-[0.2em] uppercase font-bold mt-4">Protect · Educate · Empower</p>
          <div className="flex items-center gap-3 mt-6">
            {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="w-10 h-10 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center hover:bg-[#C1272D] hover:border-[#C1272D] hover:text-white transition-colors"><Instagram size={18} /></a>}
            {settings.social_youtube && <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="w-10 h-10 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center hover:bg-[#C1272D] hover:border-[#C1272D] hover:text-white transition-colors"><Youtube size={18} /></a>}
            {settings.social_x && <a href={settings.social_x} target="_blank" rel="noopener noreferrer" data-testid="social-x" className="w-10 h-10 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center hover:bg-[#C1272D] hover:border-[#C1272D] hover:text-white transition-colors"><Twitter size={18} /></a>}
            {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="w-10 h-10 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center hover:bg-[#C1272D] hover:border-[#C1272D] hover:text-white transition-colors"><Facebook size={18} /></a>}
            <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="social-gofundme" className="w-10 h-10 rounded-full border border-[#1F4D2E]/20 flex items-center justify-center hover:bg-[#C1272D] hover:border-[#C1272D] hover:text-white transition-colors"><Heart size={18} /></a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-semibold text-[#1F4D2E] mb-4 text-sm tracking-[0.15em] uppercase">Explore</h4>
          <ul className="space-y-2.5 text-[#4F6B54]">
            <li><Link to="/projects" className="hover:text-[#C1272D] transition-colors">Sponsor a Room</Link></li>
            <li><Link to="/story" className="hover:text-[#C1272D] transition-colors">Mercy's Story</Link></li>
            <li><Link to="/transparency" className="hover:text-[#C1272D] transition-colors">Transparency Center</Link></li>
            <li><Link to="/future-campus" className="hover:text-[#C1272D] transition-colors">Future Campus</Link></li>
            <li><Link to="/contact" className="hover:text-[#C1272D] transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-semibold text-[#1F4D2E] mb-4 text-sm tracking-[0.15em] uppercase">Stay in touch</h4>
          <p className="text-[#4F6B54] mb-4 text-sm">Get honest updates on how your support becomes real impact.</p>
          <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
            <div className="relative flex-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4F6B54]/50" />
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="newsletter-email"
                className="w-full bg-white border border-[#1F4D2E]/15 rounded-full pl-9 pr-4 py-2.5 text-sm text-[#1F4D2E] placeholder:text-[#4F6B54]/60 focus:outline-none focus:border-[#C1272D]" />
            </div>
            <button disabled={busy} data-testid="newsletter-submit" className="bg-[#C1272D] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#9E1F24] transition-colors disabled:opacity-60">{busy ? "..." : "Join"}</button>
          </form>
        </div>
      </div>
      <div className="border-t border-[#1F4D2E]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#4F6B54]/80">
          {/* BOTTOM LEFT — discreet Super Admin access */}
          <Link to="/login?portal=super-admin" data-testid="super-admin-access" title="Refugium Ethiopia internal access"
            className="order-2 sm:order-1 inline-flex items-center gap-1.5 text-[#4F6B54]/50 hover:text-[#1F4D2E] transition-colors">
            <Lock size={13} /> <span className="sr-only">Super Admin access</span>
          </Link>
          <div className="order-1 sm:order-2 text-center space-y-1">
            <p>© {new Date().getFullYear()} Refugium Ethiopia. A child-protection nonprofit.</p>
            <p data-testid="footer-credit">Digital Experience by <span data-testid="footer-bycreativelabs" className="font-semibold text-[#4F6B54]">BY Creative Labs</span></p>
          </div>
          {/* BOTTOM RIGHT — discreet Employee & Volunteer login */}
          <div className="order-3 flex items-center gap-4">
            <Link to="/login?portal=employee" data-testid="employee-login" className="hover:text-[#C1272D] transition-colors">Employee Login</Link>
            <Link to="/login?portal=volunteer" data-testid="volunteer-login" className="hover:text-[#C1272D] transition-colors">Volunteer Login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
